/* eslint-disable no-nested-ternary */
import { useEffect, useState } from 'react';
import {
  Loader,
  Center,
  Text,
  Menu,
  ActionIcon,
} from '@mantine/core';
import { Page } from '@/components';
import { useTranslation } from 'react-i18next';
import { useGetAllClientsMutation } from '@/services/api';
import { getMutationResult } from '@/helpers/query';
import dayjs from 'dayjs';
import DataTableComponent from '@/components/DataTable';
import { CLIENT_STATUS } from '@/helpers/enums';
import { IconDots } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { getRouteById } from '@/helpers/routes';
import routes from '@/routes';
import { useDispatch } from 'react-redux';
import { updateBorrower } from '@/data/features/proposal/proposal';

const Clients = () => {
  const [data, setData] = useState([]);
  const [isError, setIsError] = useState(false);
  const [loading, setLoading] = useState(true);
  const pageSize = 10;
  const { t } = useTranslation();
  const [apiGetAllClients] = useGetAllClientsMutation();
  const formatedData = (date) => dayjs(date).format('DD/MM/YYYY HH:mm');
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchclients = async () => {
      try {
        const result = await getMutationResult({}, apiGetAllClients);
        const { data: { success, data: resultData } } = result;
        const clientsData = resultData;

        if (success) {
          setData(clientsData);
        } else {
          setIsError(true);
        }
      } catch (error) {
        setIsError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchclients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreateOffer = (row) => {
    const {
      fullName,
      phoneNumber,
      email: borrowerEmail,
      idNumber,
    } = row;

    const borrowers = [{
      fullName,
      phoneNumber,
      email: borrowerEmail,
      idNumber,
    }];

    dispatch(updateBorrower(borrowers));
    navigate(`${getRouteById(routes.newProposal.id, 'borrower').path}?dontReset=${true}`);
  };

  const getMenu = (row) => {
    if (row.status === 'approved' && row.offers.length === 0) {
      return (
        <Menu.Item
        size="xs"
        variant="outline"
        onClick={() => handleCreateOffer(row)}
      >
          יצירת הצעה ללקוח
        </Menu.Item>
      );
    } if (row.status === 'approved') {
      return (
        <Menu.Item
        size="xs"
        variant="outline"
        onClick={() => navigate(`${routes.proposals.path}?agentName=${encodeURIComponent(row.agentName)}&firstBorrowerName=${encodeURIComponent(row.fullName)}`)}
      >
          צפייה בהצעות לקוח
        </Menu.Item>
      );
    }

    return null;
  };

  const [columnFilters, setColumnFilters] = useState(
    {
      agentName: '',
      fullName: '',
      createdAt: [null, null],
      numberOfOffers: 0,
      status: 'approved',
    },
  );

  const columns = [
    {
      accessor: 'agentName',
      title: t('pages.clients.headers.agentName'),
      sortable: true,
      isFilterActive: true,
    },
    {
      accessor: 'fullName',
      title: t('pages.clients.headers.fullName'),
      sortable: true,
      isFilterActive: true,
    },
    {
      accessor: 'createdAt',
      title: t('pages.clients.headers.createdAt'),
      sortable: true,
      isFilterActive: true,
      render: (record) => formatedData(record.createdAt),
      filterType: 'datePicker',
    },
    {
      accessor: 'numberOfOffers',
      title: t('pages.clients.headers.numberOfOffers'),
      sortable: true,
      isFilterActive: true,
      filterType: 'numberInput',
    },
    {
      accessor: 'status',
      title: t('pages.clients.headers.status'),
      sortable: true,
      isFilterActive: true,
      filterType: 'select',
      render: (record) => CLIENT_STATUS[record.status],
      filterOptions: Object.entries(CLIENT_STATUS).map(([value, label]) => ({
        value,
        label,
      })),
    },
    {
      accessor: 'actions',
      title: t('pages.clients.headers.actions'),
      render: (record) => <Menu withinPortal position="bottom-end" shadow="sm">
        <Menu.Target>
          <ActionIcon variant="subtle">
            <IconDots size={16} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          {getMenu(record)}
        </Menu.Dropdown>
      </Menu>,
    },
  ];

  if (loading) {
    return <Center><Loader /></Center>;
  }

  if (isError) {
    return <Center><Text c="red">{t('pages.clients.noData')}</Text></Center>;
  }

  if (!data || data.length === 0) {
    return <Center><Text>{t('pages.clients.noData')}</Text></Center>;
  }

  return (
    <Page hideHelp hideNews title={t('pages.clients.title')}>
      <div>
        <p>{t('pages.clients.subtitle')}</p>
        <DataTableComponent
          columns={columns}
          data={data}
          loading={loading}
          recordsPerPage={pageSize}
          filters={columnFilters}
          setFilters={setColumnFilters}
          horizontalSpacing="sm"
        />
      </div>
    </Page>
  );
};

export default Clients;
